import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Surcharge for properties larger than 5,000 sqm (flat fee)
const LARGE_PROPERTY_THRESHOLD_SQM = 5000;
const LARGE_PROPERTY_SURCHARGE = 110;

// Travel fee tiers (returned by backend, matched here for the live UI)
const TRAVEL_TIER_FREE = 'none';
const TRAVEL_TIER_FLAT = 'flat';
const TRAVEL_TIER_CUSTOM = 'custom_quote';

export default function BookingForm({ selectedPackage, selectedAddOns = [], onClose, getTotalPrice }) {
  const [bookingDate, setBookingDate] = useState(null);
  const [bookingTime, setBookingTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [invoiceEmail, setInvoiceEmail] = useState(''); // New field for invoice email
  const [customerPhone, setCustomerPhone] = useState('');
  const [propertyAddress, setPropertyAddress] = useState(''); // New field for property address
  const [propertySizeSqm, setPropertySizeSqm] = useState(''); // New field for property size (sqm)
  const [notes, setNotes] = useState('');
  const [policyAccepted, setPolicyAccepted] = useState(false); // Cancellation policy acceptance
  const [submitting, setSubmitting] = useState(false);

  // Travel fee lookup state (Google Maps Routes API via /api/bookings/travel-quote)
  const [travelLoading, setTravelLoading] = useState(false);
  const [travelQuote, setTravelQuote] = useState(null); // { tier, travel_fee, duration_minutes, message, success, error, needs_custom_quote }

  // Debounced travel-quote lookup whenever the property address changes
  const travelDebounceRef = useRef(null);
  useEffect(() => {
    // Reset on every change
    setTravelQuote(null);
    if (travelDebounceRef.current) clearTimeout(travelDebounceRef.current);

    const trimmed = (propertyAddress || '').trim();
    if (trimmed.length < 6) {
      setTravelLoading(false);
      return;
    }

    setTravelLoading(true);
    travelDebounceRef.current = setTimeout(async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(
          `${backendUrl}/api/bookings/travel-quote?address=${encodeURIComponent(trimmed)}`
        );
        const data = await res.json();
        setTravelQuote(data);
      } catch (e) {
        setTravelQuote({ success: false, error: 'Could not estimate travel time', message: 'Could not estimate travel time' });
      } finally {
        setTravelLoading(false);
      }
    }, 800); // 800ms debounce — only fire after user stops typing

    return () => {
      if (travelDebounceRef.current) clearTimeout(travelDebounceRef.current);
    };
  }, [propertyAddress]);

  // Early return after all hooks
  if (!selectedPackage) return null;

  // Compute large-property surcharge based on entered sqm
  const sizeNum = parseInt(propertySizeSqm, 10);
  const largePropertySurcharge =
    !isNaN(sizeNum) && sizeNum > LARGE_PROPERTY_THRESHOLD_SQM ? LARGE_PROPERTY_SURCHARGE : 0;
  const baseTotal = getTotalPrice();
  const travelFee = travelQuote && travelQuote.success ? Number(travelQuote.travel_fee || 0) : 0;
  const finalTotal = baseTotal + largePropertySurcharge + travelFee;
  const blockedByCustomQuote = travelQuote && travelQuote.success && travelQuote.tier === TRAVEL_TIER_CUSTOM;

  const loadAvailableSlots = async (date) => {
    if (!date) return;
    
    setLoadingSlots(true);
    setBookingTime('');
    
    // Fix timezone issue - get date in local timezone without conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    console.log('📅 Selected date object:', date);
    console.log('📅 Sending date string to API:', dateStr);
    
    try {
      const response = await fetch(
        `${backendUrl}/api/bookings/available-slots?date=${dateStr}&package_name=${encodeURIComponent(selectedPackage.name)}`
      );
      const data = await response.json();
      console.log('📅 API Response:', data);
      console.log('📅 Available slots from API:', data.available_slots);
      setAvailableSlots(data.available_slots || []);
      
      // Auto-select first available slot
      if (data.available_slots && data.available_slots.length > 0) {
        setBookingTime(data.available_slots[0]);
      }
    } catch (error) {
      console.error('Error loading slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateChange = (date) => {
    setBookingDate(date);
    loadAvailableSlots(date);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bookingDate || !bookingTime) {
      alert('Please select a date and time');
      return;
    }

    if (availableSlots.length === 0) {
      alert('No available time slots for this date. Please choose another date.');
      return;
    }

    if (!policyAccepted) {
      alert('Please accept the cancellation policy before submitting your booking.');
      return;
    }

    if (blockedByCustomQuote) {
      alert('This property is more than 40 minutes from our base. Please contact us for a custom quote — booking is disabled for properties this far away.');
      return;
    }

    setSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const formData = new FormData();
      
      // Fix timezone issue - format date properly without UTC conversion
      const year = bookingDate.getFullYear();
      const month = String(bookingDate.getMonth() + 1).padStart(2, '0');
      const day = String(bookingDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      formData.append('customer_name', customerName);
      formData.append('customer_email', customerEmail);
      formData.append('invoice_email', invoiceEmail); // Add invoice email
      formData.append('customer_phone', customerPhone);
      formData.append('property_address', propertyAddress); // Add property address
      formData.append('property_size_sqm', propertySizeSqm || ''); // Add property size in sqm
      formData.append('package_name', selectedPackage.name);
      formData.append('package_price', baseTotal);
      formData.append('booking_date', dateStr);
      formData.append('booking_time', bookingTime);
      formData.append('add_ons', JSON.stringify(selectedAddOns.map(a => a.name) || []));
      formData.append('notes', notes);
      formData.append('policy_accepted', 'true');
      // Travel fee (server re-validates via Maps API; this is informational)
      formData.append('travel_fee', String(travelFee || 0));
      if (travelQuote && travelQuote.duration_minutes != null) {
        formData.append('travel_duration_minutes', String(travelQuote.duration_minutes));
      }

      const response = await fetch(`${backendUrl}/api/bookings`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        alert('🎉 Booking submitted successfully! Check your email for confirmation.');
        onClose();
      } else {
        alert('There was an error submitting your booking. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      alert('There was an error submitting your booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isWeekday = (date) => {
    const day = date.getDay();
    return day !== 0; // Disable Sundays (0 = Sunday)
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Book Your Session</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
            ×
          </button>
        </div>

        <div className="p-6">
          {/* Package Summary */}
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Selected Package</h3>
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-700">{selectedPackage?.name}</span>
              <span className="font-bold text-gray-900">A${selectedPackage?.price}.00</span>
            </div>
            
            {selectedAddOns.length > 0 && (
              <>
                <div className="font-semibold text-gray-900 mt-4 mb-2">Selected Add-ons:</div>
                {selectedAddOns.map((addon, idx) => (
                  <div key={idx} className="flex justify-between items-center text-sm text-gray-700">
                    <span>{addon.name}</span>
                    <span>+A${addon.price}</span>
                  </div>
                ))}
              </>
            )}
            
            <div className="border-t border-teal-200 mt-4 pt-4">
              {largePropertySurcharge > 0 && (
                <div className="flex justify-between items-center text-sm text-gray-700 mb-2">
                  <span>Large Property Surcharge (&gt;5,000 sqm)</span>
                  <span>+A${largePropertySurcharge}.00</span>
                </div>
              )}
              {travelFee > 0 && (
                <div className="flex justify-between items-center text-sm text-gray-700 mb-2">
                  <span>Travel Fee {travelQuote?.duration_minutes ? `(~${Math.round(travelQuote.duration_minutes)} min)` : ''}</span>
                  <span>+A${travelFee.toFixed(0)}.00</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="font-bold text-lg">Total:</span>
                <span className="font-bold text-2xl text-teal-600" data-testid="booking-total">A${finalTotal}.00 incl GST</span>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Email Address * 
                <span className="text-xs text-gray-500 ml-2">(Where should we send your invoice?)</span>
              </label>
              <input
                type="email"
                value={invoiceEmail}
                onChange={(e) => setInvoiceEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="invoice@company.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
              <input
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="+61 123 456 789"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Address *
                <span className="text-xs text-gray-500 ml-2">(Where is the shoot location?)</span>
              </label>
              <input
                type="text"
                value={propertyAddress}
                onChange={(e) => setPropertyAddress(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="123 Main Street, Brisbane QLD 4000"
                required
                data-testid="property-address-input"
              />

              {/* Live travel-fee feedback */}
              {travelLoading && (
                <p className="text-xs text-gray-500 mt-2" data-testid="travel-loading">
                  Estimating travel time from our base...
                </p>
              )}
              {!travelLoading && travelQuote && travelQuote.success && travelQuote.tier === TRAVEL_TIER_FREE && (
                <p className="text-xs text-emerald-700 mt-2" data-testid="travel-free">
                  ✓ Within 25 min of our base — no travel fee.
                </p>
              )}
              {!travelLoading && travelQuote && travelQuote.success && travelQuote.tier === TRAVEL_TIER_FLAT && (
                <p className="text-xs text-amber-700 mt-2" data-testid="travel-flat">
                  ⚠ Approx {Math.round(travelQuote.duration_minutes)} min from our base — a $110 travel fee has been added to your total.
                </p>
              )}
              {!travelLoading && travelQuote && travelQuote.success && travelQuote.tier === TRAVEL_TIER_CUSTOM && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="travel-custom-quote">
                  <p className="text-sm text-red-900 font-semibold">
                    Approx {Math.round(travelQuote.duration_minutes)} min from our base — custom quote required.
                  </p>
                  <p className="text-xs text-red-800 mt-1">
                    Properties more than 40 minutes away need a personalised quote. Please{' '}
                    <a href="/contact" className="underline font-medium">contact us</a>{' '}
                    or call <a href="tel:+61431323137" className="underline font-medium">+61 431 323 137</a> and
                    we'll get back to you with a tailored package.
                  </p>
                </div>
              )}
              {!travelLoading && travelQuote && !travelQuote.success && propertyAddress.length > 6 && (
                <p className="text-xs text-gray-500 mt-2" data-testid="travel-error">
                  Could not auto-estimate travel time for this address. We'll review on our side.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Size (sqm)
                <span className="text-xs text-gray-500 ml-2">(Properties over 5,000 sqm incur a $110 surcharge)</span>
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={propertySizeSqm}
                onChange={(e) => setPropertySizeSqm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="e.g. 600"
                data-testid="property-size-input"
              />
              {largePropertySurcharge > 0 && (
                <p className="text-xs text-amber-700 mt-1" data-testid="surcharge-notice">
                  Property over 5,000 sqm — a $110 surcharge has been added to your total.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Date *</label>
                <DatePicker
                  selected={bookingDate}
                  onChange={handleDateChange}
                  minDate={new Date()}
                  filterDate={isWeekday}
                  dateFormat="dd/MM/yyyy"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholderText="Choose a date"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Time * {loadingSlots && <span className="text-blue-600">(Loading...)</span>}
                </label>
                <select
                  value={bookingTime}
                  onChange={(e) => setBookingTime(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                  disabled={!bookingDate || loadingSlots}
                >
                  {!bookingDate && <option value="">Select date first</option>}
                  {bookingDate && availableSlots.length === 0 && !loadingSlots && (
                    <option value="">No slots available</option>
                  )}
                  {availableSlots.map((slot) => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
                {bookingDate && availableSlots.length === 0 && !loadingSlots && (
                  <p className="text-xs text-red-600 mt-1">No available time slots for this date. Try another day.</p>
                )}
                {availableSlots.length > 0 && (
                  <p className="text-xs text-green-600 mt-1">✅ {availableSlots.length} time slot(s) available</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Any special requirements or questions?"
              />
            </div>

            {/* Cancellation policy acknowledgement (mandatory) */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <label className="flex items-start cursor-pointer" data-testid="policy-checkbox-label">
                <input
                  type="checkbox"
                  checked={policyAccepted}
                  onChange={(e) => setPolicyAccepted(e.target.checked)}
                  className="mt-1 mr-3 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded cursor-pointer"
                  required
                  data-testid="policy-checkbox"
                />
                <span className="text-sm text-gray-800 leading-relaxed">
                  <span className="font-semibold">I agree to the cancellation policy:</span> bookings cancelled
                  or rescheduled within <span className="font-semibold">24 hours</span> of the scheduled
                  appointment will incur a <span className="font-semibold">$220 (inc GST) fee</span>.
                  Weather-related cancellations may be waived at the discretion of Matthew Minchio Media.
                </span>
              </label>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !bookingDate || !bookingTime || availableSlots.length === 0 || !policyAccepted || blockedByCustomQuote || travelLoading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg font-semibold hover:from-teal-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-booking-btn"
              >
                {submitting ? 'Submitting...' : blockedByCustomQuote ? 'Custom Quote Required' : 'Confirm Booking'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
